import React from 'react';
import { createPortal } from 'react-dom';
import { X, Printer } from 'lucide-react';

const fmtAmount = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDateTime = () => {
  const d = new Date();
  const dt = d.toLocaleDateString('ar-EG-u-nu-arab');
  const tm = d.toLocaleTimeString('ar-EG-u-nu-arab', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  return `${dt} - ${tm}`;
};

// ===================================================================
export function PrintSheetView({ sheet, onClose }) {
  const handlePrint = () => window.print();

  // ===== تجميع البنود حسب المورد مع الحفاظ على ترتيب الظهور =====
  const supplierGroups = (() => {
    const order = [];
    const map = {};
    (sheet.items || []).forEach((it) => {
      const key = (it.supplier_name || '— غير محدد —').trim();
      if (!(key in map)) {
        map[key] = [];
        order.push(key);
      }
      map[key].push(it);
    });
    return order.map((name) => ({ name, items: map[name] }));
  })();

  // ===== ضمن كل مورد: تجميع البنود المتشاركة في فاتورة واحدة (rowSpan) =====
  const buildSupplierRows = (items) => {
    const groupKeyOf = (it) => {
      if (it.group_id) return `gid:${it.group_id}`;
      const inv = (it.invoice_number || '').trim();
      if (inv) return `auto:${inv}`;
      return null;
    };
    const groupOrder = [];
    const groupMap = {};
    items.forEach((it, idx) => {
      const gk = groupKeyOf(it) || `__single_${idx}`;
      if (!(gk in groupMap)) {
        groupMap[gk] = [];
        groupOrder.push(gk);
      }
      groupMap[gk].push(it);
    });
    const ordered = groupOrder.flatMap((k) => groupMap[k]);
    const out = [];
    ordered.forEach((it, displayIdx) => {
      const gk = groupKeyOf(it);
      if (gk && groupMap[gk].length > 1) {
        const group = groupMap[gk];
        const isFirst = group[0] === it;
        if (isFirst) {
          const groupAmount = group.reduce((s, x) => s + (Number(x.amount) || 0), 0);
          out.push({ it, displayIdx, groupSpan: group.length, groupAmount, isFirstInGroup: true, isAutoGroup: !it.group_id });
        } else {
          out.push({ it, displayIdx, isFirstInGroup: false });
        }
      } else {
        out.push({ it, displayIdx, groupSpan: 1, groupAmount: Number(it.amount) || 0, isFirstInGroup: true, isAutoGroup: false });
      }
    });
    return out;
  };

  const content = (
    <div className="fixed inset-0 z-50 bg-white overflow-auto" dir="rtl" data-print-root>
      <style>{`
        @media print {
          @page { size: A4; margin: 12mm; }
          html, body { height: auto !important; overflow: visible !important; }
          body > *:not([data-print-root]) { display: none !important; }
          [data-print-root] { position: static !important; width: 100% !important; height: auto !important; overflow: visible !important; display: block !important; background: white !important; }
          .supplier-section { page-break-inside: auto; break-inside: auto; }
          .supplier-section tr, .supplier-header { page-break-inside: avoid; break-inside: avoid; }
          thead { display: table-header-group; }
          tfoot { display: table-footer-group; }
        }
      `}</style>

      {/* Toolbar (hidden on print) */}
      <div className="print:hidden bg-gray-50 border-b px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <button onClick={onClose} className="w-10 h-10 rounded-full hover:bg-gray-200 flex items-center justify-center" data-testid="close-print-sheet">
          <X className="w-5 h-5" />
        </button>
        <h2 className="flex-1 font-bold">معاينة كشف #{sheet.sheet_number}</h2>
        <button
          onClick={handlePrint}
          data-testid="print-pdf-btn"
          className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 font-semibold"
        >
          <Printer className="w-4 h-4" />
          طباعة / حفظ PDF
        </button>
      </div>

      {/* ===== Print content ===== */}
      <div className="max-w-4xl mx-auto p-6 print:p-2">
        {/* العنوان */}
        <div className="text-center pb-3 mb-5 border-b-2 border-gray-800">
          <h1 className="text-2xl print:text-xl font-extrabold">كشف تسليم فواتير للحسابات</h1>
          <p className="text-base text-gray-600 mt-1">قسم المشتريات</p>
        </div>

        {/* رأس المعلومات */}
        <div className="grid grid-cols-4 gap-4 mb-6 text-sm print:text-xs">
          <div>
            <div className="text-gray-500 text-xs mb-1">رقم الكشف</div>
            <div className="text-2xl print:text-xl font-extrabold text-purple-700">{sheet.sheet_number}</div>
          </div>
          <div>
            <div className="text-gray-500 text-xs mb-1">تاريخ التسليم</div>
            <div className="text-lg print:text-base font-bold">{sheet.sheet_date}</div>
          </div>
          <div>
            <div className="text-gray-500 text-xs mb-1">المسلم</div>
            <div className="text-lg print:text-base font-bold">{sheet.created_by || '—'}</div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="text-gray-500 text-xs mb-1">عدد الموردين</div>
              <div className="text-lg print:text-base font-bold text-emerald-700">{supplierGroups.length}</div>
            </div>
            <div>
              <div className="text-gray-500 text-xs mb-1">عدد الفواتير</div>
              <div className="text-lg print:text-base font-bold text-purple-700">{sheet.invoices_count || (sheet.items || []).length}</div>
            </div>
          </div>
        </div>

        {sheet.notes && (
          <div className="bg-gray-50 border rounded-lg p-2 mb-4 text-sm">
            <strong>ملاحظات الكشف:</strong> {sheet.notes}
          </div>
        )}

        {/* ===== الأقسام حسب المورد ===== */}
        <div className="space-y-5">
          {supplierGroups.map((group, gIdx) => {
            const rows = buildSupplierRows(group.items);
            const supplierTotal = group.items.reduce((s, x) => s + (Number(x.amount) || 0), 0);
            return (
              <div key={gIdx} className="supplier-section border border-gray-300 rounded-lg overflow-hidden">
                {/* رأس المورد */}
                <div className="supplier-header bg-purple-50 border-b border-gray-300 px-3 py-2 flex items-center justify-between text-sm">
                  <div className="font-bold text-gray-800">
                    <span className="text-purple-700">{gIdx + 1}.</span> {group.name}
                  </div>
                  <div className="text-xs text-gray-600">
                    ({group.items.length} {group.items.length === 1 ? 'فاتورة' : 'فواتير'})
                    {supplierTotal > 0 && (
                      <span className="mr-3 text-emerald-700 font-bold">إجمالي: {fmtAmount(supplierTotal)}</span>
                    )}
                  </div>
                </div>
                {/* جدول الفواتير */}
                <table className="w-full text-sm border-collapse print:text-xs">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="border-b border-l border-gray-300 px-2 py-1.5 text-right w-10">#</th>
                      <th className="border-b border-l border-gray-300 px-2 py-1.5 text-right">رقم الفاتورة</th>
                      <th className="border-b border-l border-gray-300 px-2 py-1.5 text-right">رقم الاستلام</th>
                      <th className="border-b border-l border-gray-300 px-2 py-1.5 text-right">المشروع</th>
                      <th className="border-b border-l border-gray-300 px-2 py-1.5 text-right">تاريخ الفاتورة</th>
                      <th className="border-b border-gray-300 px-2 py-1.5 text-right">المبلغ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(({ it, displayIdx, groupSpan, groupAmount, isFirstInGroup, isAutoGroup }) => {
                      const isInGroup = (groupSpan || 1) > 1 || !isFirstInGroup;
                      return (
                        <tr key={it.id || `${gIdx}-${displayIdx}`} className={isInGroup ? 'bg-purple-50/40' : ''}>
                          <td className="border-b border-l border-gray-200 px-2 py-1.5 text-center text-gray-600">{displayIdx + 1}</td>
                          {isFirstInGroup && (
                            <td rowSpan={groupSpan} className="border-b border-l border-gray-200 px-2 py-1.5 font-bold text-purple-700 align-middle text-center">
                              #{it.invoice_number || '—'}
                              {groupSpan > 1 && (
                                <div className="text-[10px] font-normal text-purple-500 mt-1">
                                  ({groupSpan} استلامات{isAutoGroup ? ' • نفس الرقم' : ''})
                                </div>
                              )}
                            </td>
                          )}
                          <td className="border-b border-l border-gray-200 px-2 py-1.5 font-semibold">{it.receipt_number || '—'}</td>
                          <td className="border-b border-l border-gray-200 px-2 py-1.5">{it.project_number || '—'}</td>
                          {isFirstInGroup && (
                            <td rowSpan={groupSpan} className="border-b border-l border-gray-200 px-2 py-1.5 align-middle text-center">
                              {it.invoice_date || '—'}
                            </td>
                          )}
                          {isFirstInGroup && (
                            <td rowSpan={groupSpan} className="border-b border-gray-200 px-2 py-1.5 text-emerald-700 font-semibold align-middle text-center">
                              {groupAmount > 0 ? fmtAmount(groupAmount) : '—'}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>

        {/* ===== التوقيعات ===== */}
        <div className="grid grid-cols-2 gap-8 mt-12 print:mt-10 text-sm">
          <div className="border-t border-gray-400 pt-2 text-center">
            <p className="text-gray-700 font-semibold">توقيع المسلم (المشتريات)</p>
            <p className="mt-6 text-gray-400">.....................................</p>
          </div>
          <div className="border-t border-gray-400 pt-2 text-center">
            <p className="text-gray-700 font-semibold">توقيع المستلم (المحاسبة)</p>
            <p className="mt-6 text-gray-400">.....................................</p>
          </div>
        </div>

        {/* footer */}
        <div className="mt-8 pt-3 border-t border-dashed border-gray-300 text-center text-xs text-gray-500">
          تم إنشاء هذا الكشف آلياً — {fmtDateTime()}
        </div>
      </div>
    </div>
  );
  return typeof document !== 'undefined' ? createPortal(content, document.body) : content;
}
